import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Link2, Loader2, RefreshCw, CheckCircle2, XCircle, Unplug, ExternalLink,
  History, AlertTriangle, Zap, Layers, Trash2, PlusCircle,
  Building2, ArrowRightLeft, ShieldAlert, Upload, FileDown, Wand2,
} from "lucide-react";
import { toast } from "sonner";

export const MYOB_TIDS = {
  card: "myob-connect-card",
  connectBtn: "myob-connect-btn",
  disconnectBtn: "myob-disconnect-btn",
  switchFileBtn: "myob-switch-file-btn",
  listFilesBtn: "myob-list-files-btn",
  companyFileRow: (id) => `myob-company-file-${id}`,
  syncAccountsBtn: "myob-sync-accounts-btn",
  syncContactsBtn: "myob-sync-contacts-btn",
  syncInvoicesBtn: "myob-sync-invoices-btn",
  syncBillsBtn: "myob-sync-bills-btn",
  syncBankBtn: "myob-sync-bank-btn",
  syncAllBtn: "myob-sync-all-btn",
  refreshBtn: "myob-refresh-status-btn",
  accountsSearch: "myob-accounts-search",
  contactsSearch: "myob-contacts-search",
  buMappingAddBtn: "myob-bu-mapping-add-btn",
  buMappingRow: (id) => `myob-bu-mapping-row-${id}`,
  buMappingBulkBtn: "myob-bu-mapping-bulk-btn",
  buMappingCsvInput: "myob-bu-mapping-csv-input",
  buMappingCsvTextarea: "myob-bu-mapping-csv-textarea",
  buMappingCsvImportBtn: "myob-bu-mapping-csv-import-btn",
  buMappingCsvTemplateLink: "myob-bu-mapping-csv-template-link",
  buMappingAutoSuggestBtn: "myob-bu-mapping-auto-suggest-btn",
  buMappingAutoApplyBtn: "myob-bu-mapping-auto-apply-btn",
};

export function MYOBConnectPanel({ canEdit }) {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const [history, setHistory] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accountsQ, setAccountsQ] = useState("");
  const [contactsQ, setContactsQ] = useState("");
  const [contactType, setContactType] = useState("");
  const [freshness, setFreshness] = useState(null);
  const [buMapping, setBuMapping] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);

  const refresh = async () => {
    try {
      const [{ data: s }, { data: h }, { data: f }] = await Promise.all([
        api.get("/myob/status"),
        api.get("/myob/sync-history?limit=10"),
        api.get("/myob/freshness"),
      ]);
      setStatus(s);
      setHistory(h);
      setFreshness(f);
    } catch (e) {
      // ignore — status endpoint returns 200 even when unconnected
    }
  };

  const loadBuMapping = async () => {
    try {
      const [{ data: m }, { data: bus }] = await Promise.all([
        api.get("/myob/bu-mapping"),
        api.get("/org/business-units").catch(() => ({ data: [] })),
      ]);
      setBuMapping(m);
      setBusinessUnits(bus || []);
    } catch { /* ignore */ }
  };

  const loadAccounts = async () => {
    try {
      const p = new URLSearchParams({ limit: "300" });
      if (accountsQ) p.set("q", accountsQ);
      const { data } = await api.get(`/myob/accounts?${p}`);
      setAccounts(data);
    } catch { /* ignore */ }
  };

  const loadContacts = async () => {
    try {
      const p = new URLSearchParams({ limit: "300" });
      if (contactsQ) p.set("q", contactsQ);
      if (contactType) p.set("contact_type", contactType);
      const { data } = await api.get(`/myob/contacts?${p}`);
      setContacts(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (status?.connected) { loadAccounts(); loadContacts(); loadBuMapping(); } /* eslint-disable-next-line */ }, [status?.connected]);
  useEffect(() => { const t = setTimeout(loadAccounts, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [accountsQ]);
  useEffect(() => { const t = setTimeout(loadContacts, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [contactsQ, contactType]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/myob/oauth/start");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start MYOB OAuth");
      setConnecting(false);
    }
  };

  const disconnect = async ({ purge = false } = {}) => {
    const msg = purge
      ? "Disconnect AND wipe cached data? Use this when switching to a different MYOB company file."
      : "Disconnect from MYOB? Cached data stays but no fresh sync will run until re-connected.";
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/myob/disconnect${purge ? "?purge=true" : ""}`);
      toast.success(purge ? "Disconnected + cache wiped" : "Disconnected from MYOB");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Disconnect failed");
    }
  };

  const switchCompanyFile = async () => {
    if (!window.confirm(
      "Switch to a different MYOB company file?\n\n" +
      "This will:\n" +
      "  1. Wipe all cached MYOB data (invoices, bills, bank accounts, GL)\n" +
      "  2. Redirect you to MYOB to authorise a new company file\n\n" +
      "Use this to move from a Sandbox file to the real GBE company file."
    )) return;
    try {
      await api.post("/myob/disconnect?purge=true");
      const { data } = await api.get("/myob/oauth/start");
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Switch failed");
    }
  };

  const [companyFiles, setCompanyFiles] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const listCompanyFiles = async () => {
    setLoadingFiles(true);
    try {
      const { data } = await api.get("/myob/company-files");
      setCompanyFiles(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not list files");
    } finally { setLoadingFiles(false); }
  };

  const runSync = async (kind) => {
    setBusyKind(kind);
    try {
      // "all" now routes through the scheduler's run-now endpoint so it gets
      // the same per-kind timeouts, in-progress guard and audit-log rows as
      // the hourly cron job. This unifies with the "Run Now" button in the
      // scheduler bar (previously "Sync everything" fire-and-forget hid the
      // fact the sync was still running for 4-5min in the background).
      if (kind === "all") {
        const { data } = await api.post("/myob/scheduler/run-now");
        if (data?.scheduled === false && data?.reason === "already_running") {
          toast.info("A sync is already running — watch the scheduler bar below for progress");
        } else {
          toast.success("Full sync started — watch the scheduler bar below (~4-5 min)");
        }
      } else {
        await api.post(`/myob/sync/${kind}`);
        toast.success(`Sync scheduled — refresh in a few seconds to see counts`);
      }
      setTimeout(() => { refresh(); loadAccounts(); loadContacts(); },
                 kind === "all" ? 6000 : 4000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed");
    } finally {
      setBusyKind(null);
    }
  };

  // Read OAuth callback query params from URL and toast a result
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("myob") === "connected") {
      if (p.get("error")) toast.error(`MYOB connect failed: ${p.get("error")}`);
      else toast.success("MYOB connected ✓");
      window.history.replaceState({}, "", window.location.pathname + "#myob");
      refresh();
    }
  }, []);

  if (!status) return <div className="h-32 rounded-lg bg-white border border-slate-200 shimmer" />;

  return (
    <div data-testid={MYOB_TIDS.card} className="space-y-4">
      {/* Header card */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${status.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {status.connected ? <CheckCircle2 className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Accounting integration</div>
              <div className="font-display text-lg text-slate-900 flex items-center gap-2">
                MYOB AccountRight Live
                {status.connected && (
                  <Badge className={status.sandbox
                    ? "bg-amber-100 text-amber-800 border-amber-200"
                    : "bg-emerald-100 text-emerald-800 border-emerald-200"}>
                    {status.sandbox ? "SANDBOX ENV" : "PRODUCTION API"}
                  </Badge>
                )}
              </div>
              {status.connected ? (
                <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                  <div>
                    Company file: <span className="font-medium text-slate-800">
                      {status.business_name || status.business_id}
                    </span>
                    {" · "}scope: <span className="font-mono">{status.scope}</span>
                  </div>
                  {status.configured_scopes && status.scope && !status.scope.includes("sme-sales") && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="myob-scope-mismatch-warning">
                      ⚠ Server requests scopes: <span className="font-mono">{status.configured_scopes}</span>. Current token is missing <span className="font-mono">sme-sales / sme-purchases / sme-banking</span>. Click <b>Disconnect</b> then <b>Connect MYOB</b> to grant the new permissions.
                    </div>
                  )}
                  <div className="text-[11px] text-slate-400">
                    Business ID: <span className="font-mono">{status.business_id}</span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500 mt-0.5">Not connected. Click Connect to authorise via MYOB.</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid={MYOB_TIDS.refreshBtn} onClick={refresh} size="sm" variant="outline" className="text-xs">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            {status.connected ? (
              canEdit && (
                <>
                  <Button data-testid={MYOB_TIDS.switchFileBtn}
                          onClick={switchCompanyFile}
                          size="sm" variant="outline"
                          className="text-xs border-amber-300 text-amber-800 hover:bg-amber-50">
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" /> Switch company file
                  </Button>
                  <Button data-testid={MYOB_TIDS.disconnectBtn}
                          onClick={() => disconnect({ purge: false })}
                          size="sm" variant="outline"
                          className="text-rose-700 border-rose-200 hover:bg-rose-50 text-xs">
                    <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                  </Button>
                </>
              )
            ) : (
              canEdit && (
                <Button data-testid={MYOB_TIDS.connectBtn} onClick={connect} disabled={connecting} className="bg-gbe hover:bg-gbe/90 text-white text-xs">
                  {connecting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Redirecting…</> : <><Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect MYOB</>}
                </Button>
              )
            )}
          </div>
        </div>

        {status.connected && status.reconnect_required && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 flex items-start gap-3"
               data-testid="myob-reconnect-banner">
            <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
              ⚠
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-amber-900">
                MYOB granular scopes — re-consent required
              </div>
              <div className="text-xs text-amber-800 mt-1 leading-relaxed">
                MYOB is switching to per-feature access. The current connection is missing{" "}
                <span className="font-mono">{(status.missing_scopes || []).join(", ")}</span>.
                Click <b>Reconnect</b> and re-approve the consent screen — takes ~30 seconds.
                No data is lost.
              </div>
              <Button size="sm" onClick={connect} disabled={connecting}
                      data-testid="myob-reconnect-btn"
                      className="mt-2 bg-amber-600 hover:bg-amber-700 text-white text-xs h-7">
                {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Reconnect &amp; grant new scopes
              </Button>
            </div>
          </div>
        )}

        {status.connected && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <MiniStat label="Accounts cached" value={status.cached?.accounts ?? 0} />
            <MiniStat label="Customers" value={status.cached?.customers ?? 0} />
            <MiniStat label="Suppliers" value={status.cached?.suppliers ?? 0} />
            <MiniStat label="Token expires" value={status.expires_in_seconds > 0 ? `${Math.floor(status.expires_in_seconds/60)}m` : "expired"} tone={status.expires_in_seconds < 300 ? "warn" : "default"} />
          </div>
        )}

        {status.connected && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                <Building2 className="w-4 h-4 text-slate-500" />
                Available company files
              </div>
              <Button size="sm" variant="outline"
                      data-testid={MYOB_TIDS.listFilesBtn}
                      onClick={listCompanyFiles} disabled={loadingFiles}>
                {loadingFiles ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> :
                                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                {companyFiles ? "Refresh" : "List files"}
              </Button>
            </div>
            {companyFiles === null ? (
              <div className="text-xs text-slate-500 italic">
                Click "List files" to see every MYOB company file this OAuth account can access. Useful for confirming
                you're on the real GBE file (not a Sandbox copy).
              </div>
            ) : companyFiles.length === 0 ? (
              <div className="text-xs text-slate-500 italic">No company files returned by MYOB.</div>
            ) : (
              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Name</th>
                      <th className="text-left px-2 py-1.5">Version</th>
                      <th className="text-left px-2 py-1.5">Country</th>
                      <th className="text-left px-2 py-1.5">Business ID</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyFiles.map((f) => (
                      <tr key={f.id} data-testid={MYOB_TIDS.companyFileRow(f.id)}
                          className={`border-t ${f.is_currently_connected ? "bg-emerald-50" : ""}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-800">
                          {f.name}
                          {(f.name || "").toLowerCase().includes("sandbox") && (
                            <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-800 border-amber-200">
                              SANDBOX FILE
                            </Badge>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">{f.product_version}</td>
                        <td className="px-2 py-1.5 text-slate-600">{f.country}</td>
                        <td className="px-2 py-1.5 text-slate-500 font-mono text-[10px]">{f.id}</td>
                        <td className="px-2 py-1.5">
                          {f.is_currently_connected ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                              CONNECTED
                            </Badge>
                          ) : (
                            <span className="text-slate-400">available</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {companyFiles.some((f) => (f.name || "").toLowerCase().includes("sandbox")
                                          && f.is_currently_connected) && (
                  <div className="p-2.5 text-xs bg-amber-50 border-t border-amber-200 text-amber-900 flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      You are currently connected to a <b>Sandbox company file</b>.
                      Click <b>Switch company file</b> above and pick the real GBE Group
                      file on the MYOB consent screen — cached data will be wiped and
                      a fresh sync will pull real numbers.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {status.connected && canEdit && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm text-slate-700">
              <b>Sync objects</b> from MYOB → local mirror. Executive KPIs re-resolve after each sync.
              <div className="text-xs text-slate-500 mt-0.5">
                Use <b>Run now</b> below to trigger the full sync (accounts + P&amp;L actuals + aged receivables).
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid={MYOB_TIDS.syncAccountsBtn} onClick={() => runSync("accounts")} disabled={busyKind === "accounts"} size="sm" variant="outline">
              {busyKind === "accounts" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Accounts
            </Button>
            {/* Contacts sync intentionally removed from the UI (Iter 51). The
                Customer/Supplier list is not read by any dashboard or KPI —
                Sales-by-BU and Debtors both pull from myob_pl_actuals /
                myob_aged_receivables. The on-demand endpoint
                `POST /api/myob/sync/contacts` still exists if it's ever
                needed. */}
            {/* Invoices/Bills sync intentionally not surfaced here — we now
                source revenue and expenses directly from myob_pl_actuals
                (MYOB's P&L Report), which matches PowerBI exactly and syncs
                in seconds. Per-invoice sync is opt-in via env
                `MYOB_SYNC_INVOICES=true` when aged-receivables detail is
                needed; on-demand endpoints `/api/myob/sync/invoices` and
                `/api/myob/sync/bills` still exist. */}
            <Button data-testid={MYOB_TIDS.syncBankBtn} onClick={() => runSync("bank-accounts")} disabled={busyKind === "bank-accounts"} size="sm" variant="outline">
              {busyKind === "bank-accounts" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
              Bank accounts
            </Button>
          </div>
          {freshness && <FreshnessMatrix freshness={freshness} />}
          <SchedulerStatusInline />
        </div>
      )}

      {status.connected && canEdit && (
        <BuMappingCard
          mapping={buMapping}
          businessUnits={businessUnits}
          onChange={loadBuMapping}
        />
      )}

      {status.connected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Accounts panel */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
              <div className="font-medium text-slate-800 text-sm">Chart of Accounts</div>
              <div className="flex items-center gap-1.5">
                <Input data-testid={MYOB_TIDS.accountsSearch} value={accountsQ} onChange={(e) => setAccountsQ(e.target.value)}
                       placeholder="Search account or GL code" className="h-7 text-xs w-48" />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {accounts.length === 0 && <div className="text-xs text-slate-400 py-8 text-center">No accounts cached — click Sync Chart of Accounts</div>}
              {accounts.map((a) => (
                <div key={a.id} className="grid grid-cols-6 gap-2 px-4 py-1.5 text-xs items-center">
                  <div className="font-mono text-slate-600">{a.display_id}</div>
                  <div className="col-span-3 text-slate-800 truncate">{a.name}</div>
                  <div className="text-slate-500">{a.classification}</div>
                  <div className="text-right font-mono text-slate-700">{a.current_balance != null ? Number(a.current_balance).toLocaleString(undefined, {maximumFractionDigits: 2}) : "—"}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Contacts panel */}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="font-medium text-slate-800 text-sm">Contacts</div>
              <div className="flex items-center gap-1.5">
                <select value={contactType} onChange={(e) => setContactType(e.target.value)}
                        className="h-7 text-xs border border-slate-200 rounded px-2 bg-white">
                  <option value="">All</option>
                  <option value="Customer">Customers</option>
                  <option value="Supplier">Suppliers</option>
                </select>
                <Input data-testid={MYOB_TIDS.contactsSearch} value={contactsQ} onChange={(e) => setContactsQ(e.target.value)}
                       placeholder="Search name / email" className="h-7 text-xs w-40" />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {contacts.length === 0 && <div className="text-xs text-slate-400 py-8 text-center">No contacts cached — click Sync Contacts</div>}
              {contacts.map((c) => (
                <div key={c.id} className="grid grid-cols-5 gap-2 px-4 py-1.5 text-xs items-center">
                  <Badge className={`text-[9px] w-fit ${c.contact_type === "Customer" ? "bg-sky-100 text-sky-800" : "bg-amber-100 text-amber-800"}`}>{c.contact_type}</Badge>
                  <div className="col-span-2 text-slate-800 truncate">{c.name || "—"}</div>
                  <div className="text-slate-500 truncate">{c.email || "—"}</div>
                  <div className="text-right font-mono text-slate-700">{c.balance_due != null ? Number(c.balance_due).toLocaleString(undefined, {maximumFractionDigits: 2}) : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
            <History className="w-4 h-4" /> Recent sync history
          </div>
          <div className="divide-y divide-slate-100 text-xs">
            {history.map((h) => (
              <div key={h.id} className="grid grid-cols-4 gap-2 py-1.5 items-start">
                <div className="font-medium text-slate-700 capitalize">{h.kind}</div>
                <div className="text-slate-500">{h.finished_at?.slice(0, 19).replace("T", " ")}</div>
                <div className={h.error ? "text-rose-700" : "text-emerald-700"}>
                  <div className="flex items-center gap-1">
                    {h.error ? <XCircle className="w-3 h-3 shrink-0" /> : <CheckCircle2 className="w-3 h-3 shrink-0" />}
                    <span>{h.error ? "Error" : `${h.count} rows`}</span>
                  </div>
                  {h.error && (
                    <div className="text-[10px] text-rose-600 mt-0.5 line-clamp-2 leading-tight"
                         title={h.error}>
                      {String(h.error).slice(0, 120)}
                    </div>
                  )}
                </div>
                <div className="text-slate-500 text-right">{h.took_seconds?.toFixed(1)}s</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone = "default" }) {
  const t = tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display text-lg ${t}`}>{value}</div>
    </div>
  );
}


// ============================================================
// Freshness matrix — per-object last sync time + count
// ============================================================
function FreshnessMatrix({ freshness }) {
  // Invoices / Bills intentionally omitted — we source revenue & expenses
  // from myob_pl_actuals (P&L Report) which is much faster and matches
  // PowerBI. Add them back here if MYOB_SYNC_INVOICES=true and you want
  // per-invoice detail surfaced on this panel.
  const KINDS = [
    { key: "accounts",      label: "Accounts" },
    { key: "contacts",      label: "Contacts" },
    { key: "bank_accounts", label: "Bank Accounts" },
    { key: "gl_monthly",    label: "GL Monthly" },
  ];
  const fmt = (iso) => {
    if (!iso) return "never";
    try {
      const dt = new Date(iso);
      const min = Math.round((Date.now() - dt.getTime()) / 60000);
      if (min < 1) return "just now";
      if (min < 60) return `${min}m ago`;
      if (min < 60 * 24) return `${Math.round(min / 60)}h ago`;
      return `${Math.round(min / 60 / 24)}d ago`;
    } catch { return iso; }
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-slate-100">
      {KINDS.map((k) => {
        const f = freshness[k.key] || {};
        const stale = f.last_sync_at
          ? (Date.now() - new Date(f.last_sync_at).getTime()) > 24 * 60 * 60 * 1000
          : true;
        return (
          <div key={k.key} data-testid={`myob-freshness-${k.key}`}
               className="rounded border border-slate-200 bg-slate-50 p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className="flex items-center justify-between mt-0.5">
              <div className="text-xs text-slate-700">
                {f.count != null ? Number(f.count).toLocaleString() : "—"} rows
              </div>
              <div className={`text-[11px] ${stale ? "text-rose-700" : "text-emerald-700"}`}>
                {fmt(f.last_sync_at)}
              </div>
            </div>
            {f.last_error && (
              <div className="text-[10px] text-rose-700 mt-1 truncate" title={f.last_error}>
                ⚠ {f.last_error.slice(0, 40)}…
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ============================================================
// BU mapping — MYOB customer/supplier/account → GBE Business Unit
// ============================================================
function BuMappingCard({ mapping, businessUnits, onChange }) {
  const [adding, setAdding] = useState(false);
  const [matchType, setMatchType] = useState("customer");
  const [matchValue, setMatchValue] = useState("");
  const [buId, setBuId] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestData, setSuggestData] = useState(null);
  const [suggestFilter, setSuggestFilter] = useState("");
  const [suggestApplying, setSuggestApplying] = useState(false);
  // per-row edits: match_value -> { included: bool, bu_id: string }
  const [suggestEdits, setSuggestEdits] = useState({});

  const editedRow = (s) => {
    const e = suggestEdits[s.match_value];
    return {
      included: e?.included ?? (!!s.suggested_bu_id && !s.already_mapped_to),
      bu_id: e?.bu_id ?? s.suggested_bu_id ?? "",
    };
  };

  const toggleIncluded = (matchValue) => {
    setSuggestEdits((prev) => {
      const cur = prev[matchValue] || {};
      const s = suggestData.suggestions.find((r) => r.match_value === matchValue);
      const defaultIncluded = !!s.suggested_bu_id && !s.already_mapped_to;
      const nextIncluded = !(cur.included ?? defaultIncluded);
      return { ...prev, [matchValue]: { ...cur, included: nextIncluded } };
    });
  };

  const setRowBu = (matchValue, buId) => {
    setSuggestEdits((prev) => {
      const cur = prev[matchValue] || {};
      // Auto-include the row when user picks a BU
      return { ...prev, [matchValue]: { ...cur, bu_id: buId, included: true } };
    });
  };

  const runAutoSuggest = async () => {
    setSuggestBusy(true); setSuggestData(null); setSuggestEdits({});
    try {
      const { data } = await api.get("/myob/bu-mapping/auto-suggest");
      setSuggestData(data);
      setSuggestOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-suggest failed");
    } finally {
      setSuggestBusy(false);
    }
  };

  const applySuggestions = async () => {
    if (!suggestData) return;
    const rows = suggestData.suggestions
      .map((s) => ({ s, edit: editedRow(s) }))
      .filter(({ edit }) => edit.included && edit.bu_id)
      .map(({ s, edit }) => ({
        match_type: "account",
        match_value: s.match_value,
        bu_id: edit.bu_id,
      }));
    if (!rows.length) {
      toast.info("Nothing to apply — tick the rows you want to include");
      return;
    }
    setSuggestApplying(true);
    try {
      const { data } = await api.post("/myob/bu-mapping/bulk-import", {
        rows, replace_existing: false,
      });
      toast.success(`Applied ${data.inserted || 0} new · ${data.updated || 0} updated`);
      onChange?.();
      setSuggestOpen(false);
      setSuggestEdits({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Apply failed");
    } finally {
      setSuggestApplying(false);
    }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setCsvText(text);
  };

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return [];
    // detect header
    const first = lines[0].toLowerCase();
    const hasHeader = first.includes("match_type") || first.includes("gl_code") ||
                       first.includes("bu_code") || first.includes("match_value");
    const body = hasHeader ? lines.slice(1) : lines;
    // support 2-col shorthand: gl_code,bu_code  (assumes match_type=account)
    const header = hasHeader ? lines[0].split(",").map((c) => c.trim().toLowerCase()) : null;
    const rows = [];
    for (const line of body) {
      const cols = line.split(",").map((c) => c.trim());
      if (header) {
        const rec = {};
        header.forEach((h, i) => { rec[h] = cols[i]; });
        rows.push({
          match_type: rec.match_type || "account",
          match_value: rec.match_value || rec.gl_code || rec.uid || "",
          bu_code: rec.bu_code || rec.bu || "",
        });
      } else if (cols.length >= 2) {
        // 2-col shorthand
        rows.push({ match_type: "account", match_value: cols[0], bu_code: cols[1] });
      }
    }
    return rows;
  };

  const runBulkImport = async () => {
    const rows = parseCsv(csvText);
    if (!rows.length) {
      toast.error("No rows detected in CSV");
      return;
    }
    setBulkBusy(true); setBulkResult(null);
    try {
      const { data } = await api.post("/myob/bu-mapping/bulk-import", {
        rows, replace_existing: replaceExisting,
      });
      setBulkResult(data);
      const total = (data.inserted || 0) + (data.updated || 0);
      toast.success(`Imported ${total} mapping(s) · ${data.skipped || 0} skipped`);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "match_type,match_value,bu_code\naccount,4-1010,ELEC\naccount,4-2010,DATA\naccount,5-1000,SOLAR\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "bu_mapping_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const add = async () => {
    if (!matchValue.trim() || !buId) {
      toast.error("Enter a value and pick a Business Unit");
      return;
    }
    try {
      await api.post("/myob/bu-mapping", {
        match_type: matchType,
        match_value: matchValue.trim(),
        business_unit_id: buId,
      });
      toast.success("Mapping saved");
      setMatchValue(""); setBuId(""); setAdding(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this mapping?")) return;
    try {
      await api.delete(`/myob/bu-mapping/${id}`);
      toast.success("Mapping removed");
      onChange?.();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const buName = (id) =>
    businessUnits.find((b) => b.id === id || b._id === id)?.name || id;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-slate-500" />
          <div className="font-medium text-slate-800 text-sm">
            Business Unit mapping
          </div>
          <span className="text-[11px] text-slate-500">
            Unmapped rows roll up to <b>GBE Group</b>.
          </span>
        </div>
        <Button size="sm" variant="outline"
                data-testid={MYOB_TIDS.buMappingAddBtn}
                onClick={() => setAdding((v) => !v)}>
          <PlusCircle className="w-3.5 h-3.5 mr-1.5" />
          {adding ? "Cancel" : "Add mapping"}
        </Button>
      </div>

      {/* Bulk import strip */}
      <div className="mb-3 flex items-center justify-between gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[11px] text-slate-600">
          <b>Bulk tools</b> — auto-suggest from account names, or upload/paste a CSV.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button"
                  data-testid={MYOB_TIDS.buMappingCsvTemplateLink}
                  onClick={downloadTemplate}
                  className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
            <FileDown className="w-3.5 h-3.5" /> Template
          </button>
          <Button size="sm" variant="outline"
                  data-testid={MYOB_TIDS.buMappingAutoSuggestBtn}
                  onClick={runAutoSuggest}
                  disabled={suggestBusy}
                  className="text-xs">
            {suggestBusy
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            Auto-suggest
          </Button>
          <Button size="sm" variant="outline"
                  data-testid={MYOB_TIDS.buMappingBulkBtn}
                  onClick={() => setBulkOpen((v) => !v)}
                  className="text-xs">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {bulkOpen ? "Close" : "Import CSV"}
          </Button>
        </div>
      </div>

      {/* Auto-suggest preview */}
      {suggestOpen && suggestData && (
        <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-slate-700">
              Scanned <b>{suggestData.summary.total_accounts}</b> accounts —
              <b className="text-emerald-700 ml-1">{suggestData.summary.matched_with_bu}</b> matched to BU,
              <b className="text-amber-700 ml-1">{suggestData.summary.matched_no_bu}</b> job# found but no matching BU,
              <b className="text-slate-500 ml-1">{suggestData.summary.unmatched}</b> no job# in name.
            </div>
            <div className="flex items-center gap-2">
              <Input value={suggestFilter}
                     onChange={(e) => setSuggestFilter(e.target.value)}
                     placeholder="Filter…" className="h-7 text-xs w-40" />
              <Button size="sm" variant="ghost" onClick={() => setSuggestOpen(false)} className="text-xs">
                <XCircle className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          <div className="max-h-80 overflow-auto border border-slate-200 rounded bg-white">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 w-8">
                    <input type="checkbox"
                           title="Select/deselect all visible"
                           checked={suggestData.suggestions
                             .filter((s) => !suggestFilter ||
                               (s.match_value + " " + (s.account_name || "") + " " + (s.suggested_bu_code || ""))
                                 .toLowerCase().includes(suggestFilter.toLowerCase()))
                             .every((s) => editedRow(s).included || !editedRow(s).bu_id)}
                           onChange={(e) => {
                             const visible = suggestData.suggestions.filter((s) => !suggestFilter ||
                               (s.match_value + " " + (s.account_name || "") + " " + (s.suggested_bu_code || ""))
                                 .toLowerCase().includes(suggestFilter.toLowerCase()));
                             setSuggestEdits((prev) => {
                               const nxt = { ...prev };
                               visible.forEach((s) => {
                                 const cur = nxt[s.match_value] || {};
                                 nxt[s.match_value] = {
                                   ...cur,
                                   included: e.target.checked && !!(cur.bu_id || s.suggested_bu_id),
                                 };
                               });
                               return nxt;
                             });
                           }} />
                  </th>
                  <th className="text-left px-2 py-1">GL Code</th>
                  <th className="text-left px-2 py-1">Account name</th>
                  <th className="text-left px-2 py-1">Job#</th>
                  <th className="text-left px-2 py-1">→ Business Unit</th>
                  <th className="text-left px-2 py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {suggestData.suggestions
                  .filter((s) => !suggestFilter ||
                    (s.match_value + " " + (s.account_name || "") + " " + (s.suggested_bu_code || ""))
                      .toLowerCase().includes(suggestFilter.toLowerCase()))
                  .map((s) => {
                    const edit = editedRow(s);
                    return (
                      <tr key={s.match_value}
                          className={
                            !edit.included ? "bg-slate-50 text-slate-400" :
                            s.conflict ? "bg-rose-50" :
                            !edit.bu_id ? "bg-amber-50" :
                            "hover:bg-emerald-50/40"
                          }>
                        <td className="px-2 py-1">
                          <input type="checkbox"
                                 checked={edit.included}
                                 disabled={!edit.bu_id}
                                 onChange={() => toggleIncluded(s.match_value)} />
                        </td>
                        <td className="px-2 py-1 font-mono">{s.match_value}</td>
                        <td className="px-2 py-1">{s.account_name}</td>
                        <td className="px-2 py-1 font-mono">{s.job_number}</td>
                        <td className="px-2 py-1">
                          <select value={edit.bu_id}
                                  onChange={(e) => setRowBu(s.match_value, e.target.value)}
                                  className="text-[11px] border border-slate-200 rounded px-1 py-0.5 bg-white max-w-[220px]">
                            <option value="">— none —</option>
                            {(businessUnits || []).map((bu) => (
                              <option key={bu.id} value={bu.id}>
                                {bu.code} · {bu.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          {!edit.included ? <span className="text-slate-400">skip</span>
                            : s.conflict && edit.bu_id === s.already_mapped_to ? <span className="text-slate-500">already mapped</span>
                            : s.already_mapped_to && edit.bu_id === s.already_mapped_to ? <span className="text-slate-500">already mapped</span>
                            : s.already_mapped_to ? <span className="text-blue-700">will overwrite</span>
                            : <span className="text-emerald-700">will apply</span>}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              Available BU codes: <span className="font-mono">{(suggestData.summary.bu_codes_available || []).join(", ") || "—"}</span>
            </div>
            <Button size="sm" onClick={applySuggestions} disabled={suggestApplying}
                    data-testid={MYOB_TIDS.buMappingAutoApplyBtn}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
              {suggestApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                <>Apply {suggestData.suggestions.filter((s) => {
                  const e = editedRow(s);
                  return e.included && e.bu_id;
                }).length} mapping(s)</>}
            </Button>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="mb-3 rounded-md border border-slate-200 bg-white p-3 space-y-2">
          <div className="text-[11px] text-slate-500">
            Accepted columns: <span className="font-mono">match_type,match_value,bu_code</span> — or shorthand two columns <span className="font-mono">gl_code,bu_code</span> (assumes account).
            BU code must match an existing Business Unit code (case-insensitive).
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] inline-flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded px-2 py-1 cursor-pointer hover:bg-slate-100">
              <Upload className="w-3.5 h-3.5" />
              Choose file
              <input type="file" accept=".csv,text/csv" className="hidden"
                     data-testid={MYOB_TIDS.buMappingCsvInput}
                     onChange={onFilePick} />
            </label>
            <label className="text-[11px] inline-flex items-center gap-1.5 text-slate-600">
              <input type="checkbox" checked={replaceExisting}
                     onChange={(e) => setReplaceExisting(e.target.checked)} />
              Wipe existing mappings before import
            </label>
          </div>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            placeholder={"match_type,match_value,bu_code\naccount,4-1010,ELEC\naccount,4-2010,DATA"}
            className="w-full min-h-[120px] text-xs font-mono border border-slate-200 rounded p-2 focus:outline-none focus:ring-1 focus:ring-slate-300"
            data-testid={MYOB_TIDS.buMappingCsvTextarea}
          />
          <div className="flex items-center justify-between">
            <div className="text-[11px] text-slate-500">
              {csvText ? `${csvText.split(/\r?\n/).filter((l) => l.trim()).length} line(s) queued` : "Empty"}
            </div>
            <Button size="sm" onClick={runBulkImport} disabled={bulkBusy || !csvText.trim()}
                    data-testid={MYOB_TIDS.buMappingCsvImportBtn}
                    className="bg-gbe hover:bg-gbe/90 text-white text-xs">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Import"}
            </Button>
          </div>
          {bulkResult && (
            <div className="text-[11px] rounded bg-slate-50 border border-slate-200 p-2 space-y-1">
              <div className="flex gap-3">
                <span><b className="text-emerald-700">{bulkResult.inserted || 0}</b> inserted</span>
                <span><b className="text-blue-700">{bulkResult.updated || 0}</b> updated</span>
                <span><b className="text-amber-700">{bulkResult.skipped || 0}</b> skipped</span>
              </div>
              {(bulkResult.errors || []).length > 0 && (
                <details>
                  <summary className="cursor-pointer text-rose-700">
                    {bulkResult.errors.length} row error(s) — click to view
                  </summary>
                  <ul className="mt-1 pl-3 space-y-0.5 max-h-32 overflow-auto">
                    {bulkResult.errors.slice(0, 20).map((er, i) => (
                      <li key={i} className="font-mono text-[10px] text-rose-700">
                        row {er.row}: {er.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {adding && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          <select value={matchType} onChange={(e) => setMatchType(e.target.value)}
                  className="h-8 text-xs border border-slate-200 rounded px-2 bg-white">
            <option value="customer">Customer UID</option>
            <option value="supplier">Supplier UID</option>
            <option value="account">Account (display_id or name)</option>
          </select>
          <Input value={matchValue} onChange={(e) => setMatchValue(e.target.value)}
                 placeholder="Paste UID or code" className="h-8 text-xs" />
          <select value={buId} onChange={(e) => setBuId(e.target.value)}
                  className="h-8 text-xs border border-slate-200 rounded px-2 bg-white">
            <option value="">— Business Unit —</option>
            {businessUnits.map((bu) => (
              <option key={bu.id || bu._id} value={bu.id || bu._id}>
                {bu.name}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={add}
                  className="bg-gbe hover:bg-gbe/90 text-white text-xs">
            Save
          </Button>
        </div>
      )}

      {mapping.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-3">
          No mappings yet — everything rolls up to the group-level BU.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {mapping.map((m) => (
            <div key={m.id} data-testid={MYOB_TIDS.buMappingRow(m.id)}
                 className="grid grid-cols-6 gap-2 py-1.5 text-xs items-center">
              <Badge className="w-fit bg-slate-100 text-slate-700">
                {m.match_type}
              </Badge>
              <div className="col-span-2 font-mono text-slate-700 truncate" title={m.match_value}>
                {m.match_value}
              </div>
              <div className="col-span-2 text-slate-800">
                → {buName(m.business_unit_id)}
              </div>
              <Button size="sm" variant="ghost"
                      onClick={() => remove(m.id)}
                      className="justify-self-end">
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
// Inline scheduler status — hourly auto-sync
// ============================================================
function SchedulerStatusInline() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  // Ref to hold the active poll interval so we can clear on unmount / completion
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/myob/scheduler/status");
      setStatus(data);
      return data;
    } catch { return null; }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/myob/scheduler/run-now");
      if (data?.scheduled === false && data?.reason === "already_running") {
        // Another run is already in flight — attach the polling loop to it
        // instead of firing a duplicate.
        toast.info("A sync is already running — I'll notify when it finishes");
      } else {
        toast.info("Sync started — will notify when complete (~2-5 minutes)");
      }
      // Kick off polling: every 5 seconds, look at the newest run's status.
      // Stop when it flips off "in_progress" (or after 6 minutes to be safe).
      stopPolling();
      const startedAt = Date.now();
      const kickoffLatest = (await load())?.recent_runs?.[0]?.started_at;
      pollRef.current = setInterval(async () => {
        const s = await load();
        const latest = s?.recent_runs?.[0];
        if (!latest) return;
        // Only react to a run that started at/after our click (avoid picking
        // up a stale finished run from before)
        if (kickoffLatest && latest.started_at < kickoffLatest) return;
        if (latest.status === "success") {
          const secs = Math.round((latest.duration_ms || 0) / 1000);
          toast.success(`Sync complete in ${secs}s — Sales by BU is fresh`);
          stopPolling();
          setRunning(false);
        } else if (latest.status === "failed") {
          toast.error(`Sync failed: ${(latest.error || "unknown").slice(0, 100)}`);
          stopPolling();
          setRunning(false);
        } else if (latest.status === "skipped") {
          toast.info(`Sync skipped: ${latest.reason || "not_connected"}`);
          stopPolling();
          setRunning(false);
        } else if (latest.status === "stale") {
          toast.warning("Previous sync was auto-recovered — click Run now to retry");
          stopPolling();
          setRunning(false);
        }
        if (Date.now() - startedAt > 6 * 60 * 1000) {
          toast.warning("Sync still running after 6 minutes — click 'Clear stuck' if it's frozen");
          stopPolling();
          setRunning(false);
        }
      }, 5000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Run failed");
      setRunning(false);
    }
  };

  const clearStuck = async () => {
    if (!window.confirm(
      "Force-clear any sync that's currently marked as running?\n\n" +
      "Use this only if a sync has been 'running' for more than 15 minutes " +
      "(usually caused by a deploy killing the sync mid-flight)."
    )) return;
    try {
      const { data } = await api.post("/myob/scheduler/clear-stuck");
      toast.success(`Cleared ${data.cleared || 0} stuck run${data.cleared === 1 ? "" : "s"}. Try Run now again.`);
      stopPolling();
      setRunning(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Clear failed");
    }
  };

  if (!status) return null;
  const next = status.next_run ? new Date(status.next_run) : null;
  const lastRun = status.recent_runs?.[0];
  const lastStatus = lastRun?.status;
  const lastAgo = lastRun?.started_at
    ? Math.round((Date.now() - new Date(lastRun.started_at).getTime()) / 60000)
    : null;

  return (
    <div className="border-t border-slate-100 pt-3 mt-1 flex items-center justify-between gap-3 text-xs">
      <div className="flex items-center gap-3 text-slate-600">
        <div className="flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-emerald-600" />
          <b>Auto-sync</b> every {status.interval_minutes}m
        </div>
        {next && (
          <div className="text-slate-500">
            next: {next.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
          </div>
        )}
        {lastRun && (
          <div className={
            lastStatus === "success" ? "text-emerald-700" :
            lastStatus === "failed" ? "text-rose-700" :
            lastStatus === "in_progress" ? "text-amber-700" :
            "text-slate-500"
          }>
            last: {lastStatus}
            {lastStatus === "in_progress" && <Loader2 className="w-3 h-3 inline animate-spin ml-1" />}
            {lastAgo != null && lastStatus !== "in_progress" && ` (${lastAgo}m ago)`}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {lastStatus === "in_progress" && (
          <Button size="sm" variant="outline"
                  onClick={clearStuck}
                  data-testid="myob-scheduler-clear-stuck"
                  title="Force-clear a sync that's been running >15 min (deploy killed it mid-flight, etc.)"
                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
            Clear stuck
          </Button>
        )}
        <Button size="sm" variant="default" onClick={runNow} disabled={running}
                data-testid="myob-scheduler-run-now"
                title={
                  "Pulls live from MYOB (~4-5 min total):\n" +
                  "  1. Chart of Accounts (BU mapping)\n" +
                  "  2. Profit & Loss Actuals — 20 months, in parallel — for Revenue / Sales-by-BU / GP% / EBITDA\n" +
                  "  3. Aged Receivables — for Executive Debtors alert\n\n" +
                  "Contacts / Bank / GL / Invoices / Bills are opt-in — none of the current dashboards read them."
                }
                className="bg-gbe hover:bg-gbe/90 text-white text-xs">
          {running ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Syncing…</> :
                     <><Zap className="w-3.5 h-3.5 mr-1" />Run now</>}
        </Button>
      </div>
    </div>
  );
}

